import _ from 'underscore';
import React, { useCallback } from 'react';
import { Ban, CheckCircle, Circle } from 'lucide-react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { Box, Button, Palette, Tooltip, Typography, useTheme } from '@mui/material';

import List from 'components/list/list';
import Checkbox from 'components/checkbox';
import ListRow from 'components/list/list-row';
import ListCell from 'components/list/list-cell';

import { useAccountState } from 'contexts/account/account-context';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import Lock from 'views/production-systems/features/farm-overview/assets/Lock';
import { Pond, ServiceSetupType } from 'views/production-systems/features/farm-setup/types/pond.type';
import { kgToLbs } from 'views/production-systems/utils/farm-overview';


const getIcon = (status: boolean, color: Palette) => {
  const Icon = status ? Ban : CheckCircle;
  return <Icon color={status ? color.darkBlue.o25 : color.bluViu.main} size={20} strokeWidth={3} />;
};

type SortablePond = Pond & {
  status: boolean;
};

type ListPondsProps = {
  isFetching: boolean;
  ponds: SortablePond[];
  setPonds: React.Dispatch<React.SetStateAction<SortablePond[]>>;
  isKg: boolean;
};

const PAGE_NAME_ID = 'productionSystems.farmSetup';

const FarmSetupPonds: React.FC<ListPondsProps> = ({ ponds, isFetching, setPonds, isKg }) => {
  const { setSelectedPonds, lockedPonds, releasePonds, selectedPonds } = usePondsState();
  const intl = useIntl();
  const theme = useTheme();
  const { account } = useAccountState();

  const handleSelectedPonds = (value: Pond, checked: boolean) => {
    setSelectedPonds(checked ? selectedPonds.concat([value.pondId]) : selectedPonds.filter((s) => s !== value.pondId));
  };

  const getDotColor = (type: ServiceSetupType, pond: SortablePond) => {
    const percentage =
      type === ServiceSetupType.AERATORS ? pond.aeratorSupportedBiomassPercentage : pond.feederSupportedBiomassPercentage;
    let color = '';
    switch (true) {
      case percentage >= 90:
        color = theme.palette.orange.main;
        break;
      case percentage < 90:
        color = theme.palette.orange.main;
        break;
      case percentage < 60:
        color = theme.palette.red.main;
        break;
      default:
        color = theme.palette.green.main;
        break;
    }

    return color;
  };

  const setupTooltip = (tooltipType: ServiceSetupType, pond: SortablePond) => {
    const boxSize = tooltipType == ServiceSetupType.AERATORS ? 220 : 190;
    const percentage =
      tooltipType == ServiceSetupType.AERATORS ? pond.aeratorSupportedBiomassPercentage : pond.feederSupportedBiomassPercentage;
    const availableQuantity = tooltipType == ServiceSetupType.AERATORS ? pond.totalNrOfAerators : pond.totalNrOfFeeders;
    const iconColor = getDotColor(tooltipType, pond);

    return (
      <Box display={'flex'} flexDirection={'column'} alignItems={'flex-start'} width={boxSize}>
        <Typography variant="caption" whiteSpace={'pretty'} color={theme.palette.gray.o25}>
          <FormattedNumber value={Number(availableQuantity)} />
          &nbsp;
          <FormattedMessage
            id={tooltipType == ServiceSetupType.AERATORS ? `${PAGE_NAME_ID}.tooltip.aerators` : `${PAGE_NAME_ID}.tooltip.feeders`}
          />
        </Typography>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} gap={2}>
          <Circle size={8} fill={iconColor} color={iconColor} />
          <Typography variant="body1" color={theme.palette.gray.main} noWrap>
            <FormattedNumber maximumFractionDigits={2} value={percentage} style={`unit`} unit="percent" />
            &nbsp;
            <FormattedMessage
              id={
                tooltipType == ServiceSetupType.AERATORS
                  ? `${PAGE_NAME_ID}.tooltip.aeratorsSupported`
                  : `${PAGE_NAME_ID}.tooltip.feedersCapacity`
              }
            />
          </Typography>
        </Box>
      </Box>
    );
  };

  const isPondDisabled = useCallback(
    (pond: Pond) => {
      return lockedPonds?.granted.some((p) => p === pond.pondId) || !!pond.lockCreatedBy;
    },
    [lockedPonds, ponds]
  );

  return (
    <List
      sx={{ height: ponds.length > 20 ? '50vh' : 'auto' }}
      isLoading={isFetching}
      noResult={{
        show: !ponds.length,
        text: intl.formatMessage({ id: 'general.noResults' }),
      }}
      rows={ponds}
      sort={setPonds}
      options={[
        { id: 'checked', label: '', autoWidth: true },
        { id: 'pondName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
        { id: 'zoneName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.zone` }) },
        { id: 'status', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.active` }), align: 'center' },
        {
          id: 'feeders',
          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feeders` }),
          align: 'right',
        },
        {
          id: 'aerators',
          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.aerators` }),
          align: 'right',
        },
        {
          id: 'aeratorSupportedBiomass',
          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.aeratorSupportedBiomass` }, {
            type: isKg ? 'kg' : 'lbs'
          }),
          align: 'right',
        },
        {
          id: 'feederSupportedBiomass',
          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feederSupportedBiomass` }, {
            type: isKg ? 'kg' : 'lbs'
          }),
          align: 'right',
        },
        {
          id: 'biomass', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.biomass` }, {
            type: isKg ? 'kg' : 'lbs'
          }), align: 'right'
        },
      ]}>
      {ponds.map((pond, idx) => (
        <ListRow
          key={idx}
          className={isPondDisabled(pond) ? 'Mui-disabled' : ''}
          sx={{ backgroundColor: isPondDisabled(pond) ? `${theme.palette.darkBlue.o25} !important` : '' }}>
          <ListCell align="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
            {isPondDisabled(pond) && account.currentUser?.email === pond.lockCreatedBy ? (
              <Button
                size="small"
                onClick={() => {
                  releasePonds([pond.pondId]);
                }}>
                <FormattedMessage id={`general.release`} />
              </Button>
            ) : (
              <Checkbox
                tabIndex={-1}
                dark
                hiddenLabel
                disabled={isPondDisabled(pond)}
                checkedIcon={isPondDisabled(pond) && <Lock />}
                checked={isPondDisabled(pond) || selectedPonds.includes(pond.pondId)}
                onChange={(_, checked) => handleSelectedPonds(pond, checked)}
              />
            )}
          </ListCell>
          <ListCell>{pond.pondName}</ListCell>
          <ListCell>{pond.zoneName}</ListCell>
          <ListCell align={'center'}>
            {getIcon(!pond.status, theme.palette)}
          </ListCell>
          <ListCell align={'right'}>
            <FormattedNumber value={Number(pond.totalNrOfFeeders)} />
          </ListCell>
          <ListCell align={'right'}>
            <FormattedNumber maximumFractionDigits={2} value={Number(pond.totalNrOfAerators)} />
          </ListCell>
          <ListCell align={'right'}>
            <Tooltip title={setupTooltip(ServiceSetupType.AERATORS, pond)}>
              <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} alignItems={'center'} gap={4}>
                <FormattedNumber maximumFractionDigits={2} value={isKg ? Number(pond.totalAeratorSupportedBiomass) : kgToLbs(Number(pond.totalAeratorSupportedBiomass))} />
                <Circle
                  size={14}
                  fill={getDotColor(ServiceSetupType.AERATORS, pond)}
                  color={getDotColor(ServiceSetupType.AERATORS, pond)}
                />
              </Box>
            </Tooltip>
          </ListCell>
          <ListCell align={'right'}>
            <Tooltip title={setupTooltip(ServiceSetupType.FEEDERS, pond)}>
              <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} alignItems={'center'} gap={4}>
                <FormattedNumber maximumFractionDigits={2} value={isKg ? Number(pond.totalBiomassFeedingCapacity) : kgToLbs(Number(pond.totalBiomassFeedingCapacity))} />
                <Circle
                  size={14}
                  fill={getDotColor(ServiceSetupType.FEEDERS, pond)}
                  color={getDotColor(ServiceSetupType.FEEDERS, pond)}
                />
              </Box>
            </Tooltip>
          </ListCell>
          <ListCell align={'right'}>
            <FormattedNumber maximumFractionDigits={2} value={isKg ? Number(pond.totalBiomassInKg) : kgToLbs(Number(pond.totalBiomassInKg))} />
          </ListCell>
        </ListRow>
      ))}
    </List>
  );
};

export default React.memo(FarmSetupPonds);
